import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { DefaultGraphStyle, ExhaustGraphChannel, GraphStyleType } from '@models/graphs';
import { GraphsService } from '@service/graphs.service';
import { ECharts, EChartsOption } from 'echarts';
import { BarChart } from 'echarts/charts';
import { TooltipComponent, GridComponent, LegendComponent } from 'echarts/components';

@Component({
    selector: 'app-cylinder-temperature',
    templateUrl: './cylinder-temperature.component.html',
    styleUrls: ['./cylinder-temperature.component.css'],
    standalone: false
})
export class CylinderTemperatureComponent implements OnInit, OnChanges {
    readonly echartsExtentions: any[];
    echartsOptions: EChartsOption = {};
    chart: ECharts = null;
    graphYAxisMax = 0;

    @Input() temperatures: ExhaustGraphChannel[];
    @Input() title: string;

    constructor(private graphService: GraphsService) {
        this.echartsExtentions = [BarChart, TooltipComponent, GridComponent, LegendComponent];
    }
    ngOnChanges(changes: SimpleChanges): void {
        if (this.chart) {
            this.updateChartOption();
        }
    }

    ngOnInit(): void {
        this.graphYAxisMax = this.findMaxYAxis();
        this.initEchartOption();
    }

    updateChartOption() {
        let newseries = this.echartsOptions;
        newseries.xAxis[0].data = this.graphService.generateTitleArr(this.temperatures);
        newseries.xAxis[1].data = this.graphService.generateValueArr(this.temperatures);
        newseries.series[0].data = this.generateSeriesValueArr();
        newseries.yAxis[0].max = this.findMaxYAxis();
        newseries.yAxis[0].min = 0;

        this.echartsOptions = newseries;
        this.chart.setOption(this.echartsOptions);
    }

    initEchartOption() {
        this.echartsOptions = {
            width: '95%',
            color: [DefaultGraphStyle.blueColor],
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'shadow',
                },
                backgroundColor: DefaultGraphStyle.tooltipBgColor,
                borderColor: DefaultGraphStyle.tooltipBorderColor,
                formatter: (params) => {
                    let result = '';
                    params.forEach((item) => {
                        result +=
                            this.graphService.getTootipBlueText(item.name) +
                            this.graphService.getTootipBlueTextBold(item.data.channel) +
                            this.graphService.getTootipWhiteText(`${item.value}&#8451`);
                    });
                    return result;
                },
                padding: 8,
            },
            xAxis: [
                {
                    type: 'category',
                    data: this.graphService.generateTitleArr(this.temperatures),
                    axisPointer: {
                        type: 'shadow',
                    },
                    position: 'bottom',
                    axisTick: {
                        show: false,
                    },
                },
                {
                    type: 'category',
                    data: this.graphService.generateValueArr(this.temperatures),
                    axisPointer: {
                        type: 'shadow',
                    },
                    position: 'bottom',
                    offset: 30,
                    axisLine: {
                        show: false,
                    },
                    axisTick: {
                        show: false,
                    },
                },
            ],
            yAxis: [
                {
                    type: 'value',
                    max: this.graphYAxisMax,
                    min: 0,
                    interval: this.graphYAxisMax / 4,
                    axisLine: {
                        lineStyle: {
                            color: DefaultGraphStyle.yAxisColor,
                        },
                    },
                    splitLine: {
                        lineStyle: {
                            color: DefaultGraphStyle.yAxisColor,
                        },
                    },
                    axisLabel: {
                        fontSize: DefaultGraphStyle.fontSize,
                        formatter: (value) => {
                            return value.toFixed();
                        },
                    },
                },
            ],
            grid: {
                left: '5%',
                tooltip: {
                    axisPointer: {
                        shadowStyle: {
                            color: DefaultGraphStyle.hoverBgColor,
                        },
                    },
                },
            },
            series: [
                {
                    data: this.generateSeriesValueArr(),
                    type: 'bar',
                    emphasis: {
                        itemStyle: {
                            color: DefaultGraphStyle.hoverColor,
                        },
                    },
                    barMaxWidth: 100,
                },
            ],
        };
    }

    onChartInit(event: ECharts) {
        this.chart = event;
    }

    generateSeriesValueArr() {
        let values = [];
        for (let i = 0; i < this.temperatures.length; i++) {
            values[i] = {
                value: this.temperatures[i].value,
                channel: this.temperatures[i].channel.opsData.name,
            };
        }

        return values;
    }

    findMaxYAxis() {
        if (this.temperatures.length > 0) {
            return Math.max(
                ...this.temperatures.map((channel) => channel.channel.fcuData.measurableRange.max),
            );
        } else {
            return 1000;
        }
    }
}
